import * as Ariakit from '@ariakit/react';
import { type ButtonProps as _ButtonProps } from '@ariakit/react';
import { Slot } from '@radix-ui/react-slot';
import classNames from 'classnames';
import React from 'react';
import { AsChildContextProvider, useAsChild } from '../../contexts';
import type { ExtendWithCommonProps } from '../../types';
import { type ResponsiveProperties } from '../../types';
import { joinClasses, responsiveClasses } from '../../utils';
import { Flex } from '../flex/flex';
import { Layout, type LayoutProps } from '../layout/layout';
import { Text } from '../text/text';
import { ButtonProvider } from './button-context';
import { ButtonIcon } from './button-icon';
import { ButtonSpinner } from './button-spinner';
import scss from './button.module.scss';

export const buttonVariants = [
  'primary',
  'secondary',
  'ghost',
  'accent',
  'brabus',
  'glass',
  'inverted',
] as const;
export type ButtonVariants = (typeof buttonVariants)[number];

export const buttonTypes = ['text', 'icon'] as const;
export type ButtonType = (typeof buttonTypes)[number];

export type ButtonProps = ExtendWithCommonProps<
  {
    /** Button's appearance variant. */
    variant?: ButtonVariants;
    /** Button's type. Use <IconButton> for icon. */
    buttonType?: ButtonType;
    /** Button's size. Default is 200. */
    mode?: ResponsiveProperties<100 | 200 | 300 | 400>;
    /** Button's content. */
    children: React.ReactNode;
    /** If true, button is loading and disabled. */
    loading?: boolean;
    /** If true, button is disabled. */
    disabled?: boolean;
    asChild?: boolean;
  } & Pick<LayoutProps, 'width' | 'height' | 'display' | 'padding' | 'margin'>,
  _ButtonProps
>;

const Button = React.forwardRef<React.ElementRef<'button'>, ButtonProps>(
  (
    {
      variant = 'primary',
      buttonType = 'text',
      mode = 200,
      loading,
      disabled,
      children,
      width,
      height,
      display,
      padding,
      margin,
      asChild,
      className,
      ...props
    },
    ref,
  ) => {
    const { hasAsChildParent } = useAsChild();

    const classes = classNames(
      scss.button,
      scss[variant],
      joinClasses(responsiveClasses('mode', mode), scss),
      {
        [scss.disabled]: loading || disabled,
        [scss['icon-button']]: buttonType === 'icon',
        [scss['text-button']]: buttonType === 'text',
      },
      className,
    );

    return (
      <ButtonProvider loading={loading} mode={mode}>
        <AsChildContextProvider hasAsChildParent={asChild}>
          {hasAsChildParent ? (
            <Ariakit.Button
              ref={ref}
              disabled={disabled || loading}
              className={className}
              {...props}
            >
              {children}
            </Ariakit.Button>
          ) : (
            <Text className={scss.label} variant="link-100" asChild>
              <Layout
                width={width}
                height={height}
                display={display}
                padding={padding}
                margin={margin}
                asChild
              >
                {asChild ? (
                  <Text variant="link-100" asChild>
                    <Flex
                      justifyContent="center"
                      alignItems="center"
                      gap={50}
                      width={'fit-content'}
                      asChild
                    >
                      <Slot className={classes} {...props}>
                        {children}
                      </Slot>
                    </Flex>
                  </Text>
                ) : (
                  <Ariakit.Button
                    ref={ref}
                    disabled={disabled || loading}
                    className={classes}
                    {...props}
                  >
                    <Text variant="link-100" asChild>
                      <Flex
                        justifyContent="center"
                        alignItems="center"
                        gap={50}
                        width="full"
                      >
                        {children}
                      </Flex>
                    </Text>
                  </Ariakit.Button>
                )}
              </Layout>
            </Text>
          )}
        </AsChildContextProvider>
      </ButtonProvider>
    );
  },
);

/**
 * Trigger an action or event.
 *
 * Composed of:
 * - `<Button.Icon>` to add an icon to the button. If loading, the icon will be replaced by a spinner.
 * - `<Button.Spinner>` to add a spinner to the button. If loading, the spinner will be displayed.
 *
 * @link https://prod.web-components.srv.smart.com/?path=/docs/components-button--docs
 */
const ButtonNamespace = Object.assign(Button, {
  Icon: ButtonIcon,
  Spinner: ButtonSpinner,
});
export { ButtonNamespace as Button };

Button.displayName = 'Button';
